/**
 * Phase 5 AI-02 — SSE consumer hook for POST /api/ai/chat.
 *
 * Uses fetch + ReadableStream rather than EventSource because:
 *   - EventSource doesn't support POST (we need a body).
 *   - EventSource doesn't send credentials (we need session cookie).
 *
 * Parses each SSE `data:` line through SseEventSchema; ignores parse failures
 * (defence-in-depth: malformed events from a future server version don't
 * crash the client). Handles the abort signal both client-initiated (Stop
 * button) and from page navigation cleanup.
 */

import { useEffect, useRef, useState, useCallback } from 'react';
import { SseEventSchema, type SseEvent, type ChatImage } from '@stell/shared';

export interface AssistantToolCall {
  id: string;
  name: string;
  status: 'pending' | 'done';
  args: unknown;
  duration_ms?: number;
  result_summary?: string;
}

/** AI-21 (#193): dataen i én `memory_changed`-hendelse — brikken under svaret. */
export type MemoryChange = Extract<SseEvent, { event: 'memory_changed' }>['data'];

/**
 * #212: én brikke i tråden. En live-hendelse har notatets `id` (Angre kan
 * slette det); en brikke hydrert fra historikken (`hydrateThread`) har ingen
 * `id`, og dermed aldri Angre — notatet kan være slettet, endret eller flyttet
 * siden turen.
 */
export type MemoryChipEntry = Omit<MemoryChange, 'id'> & { id?: string | undefined };

export interface StreamState {
  status: 'idle' | 'streaming' | 'done' | 'error' | 'cancelled';
  currentText: string;
  filterCorrected: boolean;
  toolCalls: AssistantToolCall[];
  /** Notater turen lagret, foreslo eller glemte, i rekkefølgen de kom (#193). */
  memoryChanges: MemoryChange[];
  finishReason?: string;
  costUsd?: number;
  foodAdjacent?: boolean;
  errorCode?: string;
  /**
   * Raw error detail. NOT safe to show a family member directly: the two local
   * failure paths below put technical strings here (`HTTP 500`, or a raw
   * `Error.message` such as "Failed to fetch"). Only render it when
   * `errorFromServer` is true — that is the curated Bokmål `message_nb` from
   * the SSE error event. Otherwise use the i18n copy keyed by `errorCode`.
   */
  errorMessage?: string;
  /** True only when errorCode/errorMessage came from a server SSE error event. */
  errorFromServer?: boolean;
  retryAfterSeconds?: number;
  conversationId?: string;
}

const initial: StreamState = {
  status: 'idle',
  currentText: '',
  filterCorrected: false,
  toolCalls: [],
  memoryChanges: [],
};

function parseSseChunk(buffer: string): { events: SseEvent[]; rest: string } {
  // SSE messages are delimited by blank line (\n\n).
  const events: SseEvent[] = [];
  const blocks = buffer.split('\n\n');
  const rest = blocks.pop() ?? '';
  for (const block of blocks) {
    if (block.startsWith(':')) continue; // heartbeat ping
    let event = '';
    let dataLine = '';
    for (const line of block.split('\n')) {
      if (line.startsWith('event:')) event = line.slice(6).trim();
      else if (line.startsWith('data:')) dataLine += line.slice(5).trim();
    }
    if (!event || !dataLine) continue;
    try {
      const data = JSON.parse(dataLine);
      const parsed = SseEventSchema.safeParse({ event, data });
      if (parsed.success) events.push(parsed.data);
    } catch {
      // bad JSON — drop silently (defence-in-depth)
    }
  }
  return { events, rest };
}

/** Lokal feil (ikke fra serveren) — errorMessage er teknisk og vises aldri. */
function clientError(errorCode: string, errorMessage: string) {
  return (s: StreamState): StreamState => ({
    ...s,
    status: 'error',
    errorCode,
    errorMessage,
    errorFromServer: false,
  });
}

function handleEvent(
  ev: SseEvent,
  setState: (fn: (s: StreamState) => StreamState) => void,
) {
  switch (ev.event) {
    case 'conversation_created':
      setState((s) => ({ ...s, conversationId: ev.data.id }));
      break;
    case 'delta':
      setState((s) => ({ ...s, currentText: s.currentText + ev.data.text }));
      break;
    case 'tool_call_start':
      setState((s) => ({
        ...s,
        toolCalls: [
          ...s.toolCalls,
          { id: ev.data.id, name: ev.data.name, status: 'pending', args: ev.data.args },
        ],
      }));
      break;
    case 'tool_call_end':
      setState((s) => ({
        ...s,
        toolCalls: s.toolCalls.map((t) =>
          t.id === ev.data.id
            ? { ...t, status: 'done', duration_ms: ev.data.duration_ms, result_summary: ev.data.result_summary }
            : t,
        ),
      }));
      break;
    case 'filter_correction':
      // #136 F2: kutt det som alt var vist av runden filteret stoppet, så neste
      // rundes svar ikke limes på utkastet. Tidligere runder i turen står igjen.
      setState((s) => ({
        ...s,
        filterCorrected: true,
        currentText: s.currentText.slice(
          0,
          Math.max(0, s.currentText.length - ev.data.discard_chars),
        ),
      }));
      break;
    case 'memory_changed':
      // Sendes etter commit (Pattern 2); ingenting sender den før #192.
      setState((s) => ({ ...s, memoryChanges: [...s.memoryChanges, ev.data] }));
      break;
    case 'round_advance':
      // Diagnostic — UI ignores in v1.
      break;
    case 'done':
      setState((s) => ({
        ...s,
        status: 'done',
        finishReason: ev.data.finish_reason,
        costUsd: ev.data.cost_usd,
        foodAdjacent: ev.data.food_adjacent,
      }));
      break;
    case 'error':
      setState((s) => ({
        ...s,
        status: 'error',
        errorCode: ev.data.code,
        // message_nb is curated Bokmål from the D-13 error matrix — safe to show.
        errorMessage: ev.data.message_nb,
        errorFromServer: true,
        ...(ev.data.retry_after_seconds !== undefined
          ? { retryAfterSeconds: ev.data.retry_after_seconds }
          : {}),
      }));
      break;
  }
}

export function useAssistantStream() {
  const [state, setState] = useState<StreamState>(initial);
  const abortRef = useRef<AbortController | null>(null);

  const resetTo = useCallback((conversationId?: string) => {
    const ctl = abortRef.current;
    abortRef.current = null;
    ctl?.abort();
    if (conversationId) {
      setState({ ...initial, conversationId });
      return;
    }
    setState(initial);
  }, []);

  const start = useCallback(
    async (input: { message: string; conversationId?: string; image?: ChatImage; assigneeId?: string }) => {
      abortRef.current?.abort();
      const ctl = new AbortController();
      abortRef.current = ctl;
      setState({
        ...initial,
        status: 'streaming',
        ...(input.conversationId !== undefined ? { conversationId: input.conversationId } : {}),
      });
      try {
        const res = await fetch('/api/ai/chat', {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            conversation_id: input.conversationId,
            message: input.message,
            ...(input.image ? { image: input.image } : {}),
            ...(input.assigneeId ? { assignee_id: input.assigneeId } : {}),
          }),
          signal: ctl.signal,
        });
        if (!res.ok || !res.body) {
          setState(clientError('http_error', `HTTP ${res.status}`));
          return;
        }
        const reader = res.body.getReader();
        const dec = new TextDecoder();
        let buffer = '';
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          buffer += dec.decode(value, { stream: true });
          const { events, rest } = parseSseChunk(buffer);
          buffer = rest;
          for (const ev of events) handleEvent(ev, setState);
        }
        // Lukket uten 'done'/'error' (krasj, tapt event): ellers står svaret og
        // venter for alltid. En avbrutt strøm eies av cancel()/neste start().
        if (!ctl.signal.aborted) {
          setState((s) =>
            s.status === 'streaming' ? clientError('network', 'stream closed without done')(s) : s,
          );
        }
      } catch (err) {
        if ((err as Error).name === 'AbortError') {
          if (abortRef.current === ctl) {
            setState((s) => ({ ...s, status: 'cancelled' }));
          }
        } else {
          setState(clientError('network', (err as Error).message));
        }
      } finally {
        if (abortRef.current === ctl) {
          abortRef.current = null;
        }
      }
    },
    [],
  );

  const cancel = useCallback(() => {
    abortRef.current?.abort();
  }, []);

  const resetConversation = useCallback(() => {
    resetTo();
  }, [resetTo]);

  const restoreConversation = useCallback((conversationId: string) => {
    resetTo(conversationId);
  }, [resetTo]);

  // Cleanup on unmount
  useEffect(
    () => () => {
      abortRef.current?.abort();
    },
    [],
  );

  return { state, start, cancel, resetConversation, restoreConversation };
}
